import { fetchJson, postJson } from '../lib/api-client';
import { showNotification } from '../lib/helpers';

interface UnitFormDataResponse {
    success: boolean;
    unitTypes: Array<{ id: number; name: string }>;
    classifications: Array<{ key: string; name: string }>;
    services: Array<{ key: string; name: string }>;
}

const ACTIVE_SVG   = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="32" stroke-width="1.5" stroke="#10B981"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>';
const INACTIVE_SVG = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="32" stroke-width="1.5" stroke="#E11D48"><path stroke-linecap="round" stroke-linejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" /></svg>';

const isNarrow = () => !window.matchMedia('(min-width: 992px)').matches;

function populateSelect(
    sel: HTMLSelectElement | null,
    options: Array<{ value: string; text: string }>,
    currentValue: string
): void {
    if (!sel) return;
    sel.innerHTML = '';
    sel.add(new Option('—', '', currentValue === '', currentValue === ''));
    options.forEach(o => {
        const selected = o.value === currentValue;
        sel.add(new Option(o.text, o.value, selected, selected));
    });
}

export function init(): void {
    const container = (
        document.getElementById('offer-index-container') ??
        document.getElementById('offer-details-container')
    ) as HTMLElement | null;
    if (!container) return;

    const urlUnitFormData        = container.dataset.urlUnitFormData ?? '';
    const urlEditUnit            = container.dataset.urlEditUnit     ?? '';
    const urlEditPrice           = container.dataset.urlEditPrice    ?? '';
    const capsShowClassification = container.dataset.capsShowClassification === 'true';
    const capsShowService        = container.dataset.capsShowService        === 'true';
    const capsShowDateRange      = container.dataset.capsShowDateRange      === 'true';
    const capsShowSeatPlaces     = container.dataset.capsShowSeatPlaces     === 'true';

    const unitFormDataCache: Record<number, UnitFormDataResponse> = {};

    // ── Unit modal DOM refs ───────────────────────────────────────────────────
    const unitModalEl      = document.getElementById('editUnitModal');
    const unitLoadingEl    = document.getElementById('edit-unit-loading');
    const unitFormEl       = document.getElementById('edit-unit-form') as HTMLFormElement | null;
    const saveUnitBtn      = document.getElementById('edit-unit-save-btn') as HTMLButtonElement | null;
    const unitIdEl         = document.getElementById('edit-unit-id')         as HTMLInputElement | null;
    const unitOfferIdEl    = document.getElementById('edit-unit-offer-id')   as HTMLInputElement | null;
    const unitOfferGroupEl = document.getElementById('edit-unit-offer-group') as HTMLInputElement | null;
    const unitNameEl       = document.getElementById('edit-unit-name')        as HTMLInputElement | null;
    const unitTypeSelect   = document.getElementById('edit-unit-type-select') as HTMLSelectElement | null;
    const unitClassSelect  = document.getElementById('edit-unit-classification-select') as HTMLSelectElement | null;
    const unitSvcSelect    = document.getElementById('edit-unit-service-select') as HTMLSelectElement | null;
    const unitActiveCheck  = document.getElementById('edit-unit-active-check') as HTMLInputElement | null;
    const classGroupEl     = document.getElementById('edit-unit-classification-group');
    const serviceGroupEl   = document.getElementById('edit-unit-service-group');

    // ── Price modal DOM refs ──────────────────────────────────────────────────
    const priceModalEl      = document.getElementById('editPriceModal');
    const priceFormEl       = document.getElementById('edit-price-form') as HTMLFormElement | null;
    const savePriceBtn      = document.getElementById('edit-price-save-btn') as HTMLButtonElement | null;
    const priceIdEl         = document.getElementById('edit-price-id')          as HTMLInputElement | null;
    const priceUnitIdEl     = document.getElementById('edit-price-unit-id')     as HTMLInputElement | null;
    const priceOfferIdEl    = document.getElementById('edit-price-offer-id')    as HTMLInputElement | null;
    const priceOfferGroupEl = document.getElementById('edit-price-offer-group') as HTMLInputElement | null;
    const priceValueEl      = document.getElementById('edit-price-price')       as HTMLInputElement | null;
    const partnerValueEl    = document.getElementById('edit-price-partner')     as HTMLInputElement | null;
    const priceFromEl       = document.getElementById('edit-price-from')        as HTMLInputElement | null;
    const priceToEl         = document.getElementById('edit-price-to')          as HTMLInputElement | null;
    const priceStatusSel    = document.getElementById('edit-price-status')      as HTMLSelectElement | null;
    const priceSeatsEl      = document.getElementById('edit-price-seat-places') as HTMLInputElement | null;
    const pricePartnerGrp   = document.getElementById('edit-price-partner-group');
    const priceDateGrp      = document.getElementById('edit-price-date-group');
    const priceSeatGrp      = document.getElementById('edit-price-seat-places-group');

    if (!unitModalEl || !priceModalEl) return;

    let currentUnitRow: HTMLElement | null  = null;
    let currentPriceRow: HTMLElement | null = null;

    // ── Click delegation ──────────────────────────────────────────────────────
    document.addEventListener('click', async (e) => {
        const target = e.target as HTMLElement;

        // ── Open unit edit modal ──────────────────────────────────────────────
        if (target.closest('.btn-edit-unit-inline')) {
            if (!isNarrow()) return;

            const row = target.closest<HTMLElement>('.unit-row');
            if (!row) return;
            currentUnitRow = row;

            const offerId    = parseInt(row.dataset.offerId ?? '0', 10);
            const offerGroup = row.dataset.offerGroup ?? '';

            unitLoadingEl?.classList.remove('d-none');
            unitFormEl?.classList.add('d-none');
            new window.bootstrap.Modal(unitModalEl).show();

            if (!unitFormDataCache[offerId]) {
                try {
                    const data = await fetchJson<UnitFormDataResponse>(`${urlUnitFormData}?offerId=${offerId}`);
                    if (data.success) unitFormDataCache[offerId] = data;
                } catch { /* ignore */ }
            }

            const fd = unitFormDataCache[offerId];

            populateSelect(
                unitTypeSelect,
                fd ? fd.unitTypes.map(t => ({ value: String(t.id), text: t.name })) : [],
                row.dataset.typeId ?? ''
            );
            populateSelect(
                unitClassSelect,
                fd ? fd.classifications.map(c => ({ value: c.key, text: c.name })) : [],
                row.dataset.classification ?? ''
            );
            populateSelect(
                unitSvcSelect,
                fd ? fd.services.map(s => ({ value: s.key, text: s.name })) : [],
                row.dataset.service ?? ''
            );

            if (unitIdEl)         unitIdEl.value         = row.dataset.unitId  ?? '';
            if (unitOfferIdEl)    unitOfferIdEl.value    = String(offerId);
            if (unitOfferGroupEl) unitOfferGroupEl.value = offerGroup;
            if (unitNameEl) {
                // Prefer the hidden cell-edit input value (has current text), fall back to display span
                const inlineName = row.querySelector<HTMLInputElement>('[name="name"]');
                unitNameEl.value = inlineName?.value ?? row.querySelector('.display-name')?.textContent?.trim() ?? '';
            }
            if (unitActiveCheck) unitActiveCheck.checked = row.dataset.active !== 'false';

            classGroupEl?.classList.toggle('d-none', !capsShowClassification);
            serviceGroupEl?.classList.toggle('d-none', !capsShowService);

            unitLoadingEl?.classList.add('d-none');
            unitFormEl?.classList.remove('d-none');
            return;
        }

        // ── Open price edit modal ─────────────────────────────────────────────
        if (target.closest('.btn-edit-price-inline')) {
            if (!isNarrow()) return;

            const row = target.closest<HTMLElement>('.price-row');
            if (!row) return;
            currentPriceRow = row;

            const agencyAcceptsPartnership = row.dataset.agencyAcceptsPartnership !== 'false';

            if (priceIdEl)         priceIdEl.value         = row.dataset.priceId    ?? '';
            if (priceUnitIdEl)     priceUnitIdEl.value     = row.dataset.unitId     ?? '';
            if (priceOfferIdEl)    priceOfferIdEl.value    = row.dataset.offerId    ?? '';
            if (priceOfferGroupEl) priceOfferGroupEl.value = row.dataset.offerGroup ?? '';

            // Read current values from the hidden cell-edit inputs already in the row DOM
            const inlinePrice   = row.querySelector<HTMLInputElement>('[name="price"]');
            const inlinePartner = row.querySelector<HTMLInputElement>('[name="partnerPriceOverride"]');
            const inlineSeats   = row.querySelector<HTMLInputElement>('[name="numberOfSeats"]');
            const inlineStatus  = row.querySelector<HTMLSelectElement>('[name="status"]');

            if (priceValueEl)  priceValueEl.value  = inlinePrice?.value   ?? '';
            if (partnerValueEl) partnerValueEl.value = inlinePartner?.value ?? '';
            if (priceFromEl)   priceFromEl.value   = row.dataset.from     ?? '';
            if (priceToEl)     priceToEl.value     = row.dataset.to       ?? '';
            if (priceStatusSel) {
                priceStatusSel.value = row.dataset.status ?? '1';
                if (priceStatusSel.value === '') priceStatusSel.value = '0';
            }
            if (priceSeatsEl)  priceSeatsEl.value  = inlineSeats?.value   ?? '';

            pricePartnerGrp?.classList.toggle('d-none', !agencyAcceptsPartnership);
            priceDateGrp?.classList.toggle('d-none', !capsShowDateRange);
            priceSeatGrp?.classList.toggle('d-none', !capsShowSeatPlaces);

            new window.bootstrap.Modal(priceModalEl).show();
            return;
        }
    });

    // ── Save unit ─────────────────────────────────────────────────────────────
    saveUnitBtn?.addEventListener('click', async () => {
        const row = currentUnitRow;
        if (!row || !saveUnitBtn) return;

        const offerId    = parseInt(unitOfferIdEl?.value ?? '0', 10);
        const offerGroup = unitOfferGroupEl?.value ?? '';
        const nameVal    = unitNameEl?.value ?? '';
        const typeVal    = parseInt(unitTypeSelect?.value ?? '0', 10);
        const classVal   = unitClassSelect?.value ?? '';
        const svcVal     = unitSvcSelect?.value   ?? '';
        const active     = unitActiveCheck?.checked ?? false;

        const payload = {
            id:                   parseInt(unitIdEl?.value ?? '0', 10),
            offerId,
            name:                 nameVal,
            offerUnitTypeId:      typeVal,
            classification:       classVal,
            service:              svcVal,
            active,
            jsonCustomAttributes: '',
            offerGroupName:       offerGroup,
            groupData:            {} as Record<string, string>,
        };

        saveUnitBtn.disabled = true;
        try {
            const result = await postJson(urlEditUnit, payload);
            if (result.success) {
                const dispName = row.querySelector('.display-name');
                if (dispName) dispName.textContent = nameVal;

                const dispType = row.querySelector('.display-type');
                if (dispType && unitTypeSelect)
                    dispType.textContent = unitTypeSelect.options[unitTypeSelect.selectedIndex]?.text ?? '';

                const dispClass = row.querySelector('.display-class');
                if (dispClass) dispClass.textContent = classVal;

                const dispSvc = row.querySelector('.display-service');
                if (dispSvc) dispSvc.textContent = svcVal;

                const dispActive = row.querySelector('.display-active');
                if (dispActive) dispActive.innerHTML = active ? ACTIVE_SVG : INACTIVE_SVG;

                // Update data attrs so subsequent edits (inline or modal) see fresh values
                row.dataset.typeId         = String(typeVal);
                row.dataset.classification = classVal;
                row.dataset.service        = svcVal;
                row.dataset.active         = String(active);

                window.bootstrap.Modal.getInstance(unitModalEl)?.hide();
                showNotification('Unit updated.', 'success');
            } else {
                showNotification('Failed to update unit.', 'error');
            }
        } catch {
            showNotification('An error occurred.', 'error');
        }
        saveUnitBtn.disabled = false;
    });

    // ── Save price ────────────────────────────────────────────────────────────
    savePriceBtn?.addEventListener('click', async () => {
        const row = currentPriceRow;
        if (!row || !savePriceBtn) return;

        const agencyAcceptsPartnership = row.dataset.agencyAcceptsPartnership !== 'false';
        const offerGroup = priceOfferGroupEl?.value ?? '';
        const priceVal   = parseFloat(priceValueEl?.value  ?? '0') || 0;
        const partnerVal = agencyAcceptsPartnership
            ? (parseFloat(partnerValueEl?.value ?? '0') || 0)
            : priceVal;
        const from   = priceFromEl?.value    ?? '';
        const to     = priceToEl?.value      ?? '';
        const status = parseInt(priceStatusSel?.value ?? '1', 10);
        const seats  = offerGroup === 'Transport'
            ? (parseInt(priceSeatsEl?.value ?? '1', 10) || null)
            : null;

        const payload = {
            id:                   parseInt(priceIdEl?.value    ?? '0', 10),
            offerUnitId:          parseInt(priceUnitIdEl?.value ?? '0', 10),
            offerId:              parseInt(priceOfferIdEl?.value ?? '0', 10),
            price:                priceVal,
            partnerPriceOverride: partnerVal,
            from,
            to,
            status,
            numberOfSeats:        seats,
            offerGroupName:       offerGroup,
        };

        savePriceBtn.disabled = true;
        try {
            const result = await postJson(urlEditPrice, payload);
            if (result.success) {
                const locActive   = container.dataset.locActive   ?? 'Active';
                const locInactive = container.dataset.locInactive ?? 'Inactive';

                const disp = (cls: string) => row.querySelector(cls);
                const priceDisplay   = disp('.display-price');
                const partnerDisplay = disp('.display-partner');
                const fromDisplay    = disp('.display-from');
                const toDisplay      = disp('.display-to');
                const statusDisplay  = disp('.display-status');
                const seatsDisplay   = disp('.display-seats');

                if (priceDisplay)   priceDisplay.textContent   = String(priceVal);
                if (partnerDisplay) partnerDisplay.textContent = ` / ${partnerVal}`;
                if (fromDisplay)    fromDisplay.textContent    = from.split('-').reverse().join('.');
                if (toDisplay)      toDisplay.textContent      = to.split('-').reverse().join('.');
                if (statusDisplay)  statusDisplay.innerHTML    = status === 1
                    ? `<span class="badge bg-success">${locActive}</span>`
                    : `<span class="badge bg-secondary">${locInactive}</span>`;
                if (seatsDisplay && priceSeatsEl)
                    seatsDisplay.textContent = priceSeatsEl.value;

                // Keep data attrs and hidden cell-edit inputs in sync for subsequent edits
                row.dataset.from   = from;
                row.dataset.to     = to;
                row.dataset.status = String(status);

                const inlinePrice   = row.querySelector<HTMLInputElement>('[name="price"]');
                const inlinePartner = row.querySelector<HTMLInputElement>('[name="partnerPriceOverride"]');
                const inlineStatus  = row.querySelector<HTMLSelectElement>('[name="status"]');
                const inlineSeats   = row.querySelector<HTMLInputElement>('[name="numberOfSeats"]');
                if (inlinePrice)   inlinePrice.value   = String(priceVal);
                if (inlinePartner) inlinePartner.value = String(partnerVal);
                if (inlineStatus)  inlineStatus.value  = String(status);
                if (inlineSeats && seats !== null) inlineSeats.value = String(seats);

                window.bootstrap.Modal.getInstance(priceModalEl)?.hide();
                showNotification('Price updated.', 'success');
            } else {
                showNotification('Failed to update price.', 'error');
            }
        } catch {
            showNotification('An error occurred.', 'error');
        }
        savePriceBtn.disabled = false;
    });
}
